import { motion } from 'framer-motion'

interface Option<T extends string> {
  value: T
  label: string
  description?: string
  emoji?: string
}

interface SingleSelectProps<T extends string> {
  options: Option<T>[]
  value: T | null
  onChange: (value: T) => void
}

export function SingleSelect<T extends string>({
  options,
  value,
  onChange,
}: SingleSelectProps<T>) {
  return (
    <div className="flex flex-col gap-3">
      {options.map((option, index) => {
        const isSelected = value === option.value

        return (
          <motion.button
            key={option.value}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.04 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => onChange(option.value)}
            className={`
              w-full min-h-[60px] px-5 py-4 rounded-xl transition-all duration-200
              border-2 text-left
              ${isSelected
                ? 'border-primary bg-teal-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
              }
            `}
          >
            <div className="flex items-center gap-4">
              {option.emoji && (
                <span className="text-2xl">{option.emoji}</span>
              )}
              <div className="flex-1">
                <span className={`font-medium text-[16px] ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>
                  {option.label}
                </span>
                {option.description && (
                  <p className="text-sm mt-0.5 text-gray-500">
                    {option.description}
                  </p>
                )}
              </div>
              <div className={`
                w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-200
                ${isSelected ? 'border-primary bg-primary' : 'border-gray-300'}
              `}>
                {isSelected && (
                  <motion.svg
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="w-3.5 h-3.5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </motion.svg>
                )}
              </div>
            </div>
          </motion.button>
        )
      })}
    </div>
  )
}
